import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Home, Building, Castle, Mountain, Warehouse, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { API_BASE_URL } from "@/config/api";

/**
 * ValuationPage - Multi-step property valuation journey for sellers
 * Hidden page, not in navigation - accessible via homepage entry or direct URL
 */
const ValuationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1 - Location
    address: searchParams.get("address") || "",
    // Step 2 - Property Type
    propertyType: "",
    // Step 3 - Characteristics
    surface: "",
    bedrooms: "",
    rooms: "",
    condition: "",
    yearBuilt: "",
    features: [],
    // Step 4 - Timing
    timing: "",
    // Step 5 - Contact
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  const steps = [
    { id: 1, label: "Localisation" },
    { id: 2, label: "Type de bien" },
    { id: 3, label: "Caractéristiques" },
    { id: 4, label: "Calendrier" },
    { id: 5, label: "Contact" },
  ];

  const propertyTypes = [
    { id: "apartment", label: "Appartement", icon: Building },
    { id: "house", label: "Maison", icon: Home },
    { id: "villa", label: "Villa", icon: Castle },
    { id: "exceptional", label: "Propriété d'exception", icon: Mountain },
    { id: "loft", label: "Loft", icon: Warehouse },
    { id: "chalet", label: "Chalet", icon: Mountain },
  ];

  const conditionOptions = [
    { id: "new", label: "Neuf / Rénové récemment" },
    { id: "good", label: "Bon état" },
    { id: "renovation", label: "À rafraîchir" },
    { id: "major", label: "Travaux importants à prévoir" },
  ];

  const timingOptions = [
    { id: "asap", label: "Dès que possible" },
    { id: "6months", label: "Dans les 6 mois" },
    { id: "later", label: "Plus tard" },
    { id: "thinking", label: "En réflexion" },
  ];

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/estimation`, {
        address: formData.address,
        property_type: formData.propertyType,
        rooms: formData.rooms ? Number(formData.rooms) : null,
        bedrooms: formData.bedrooms ? Number(formData.bedrooms) : null,
        size_sqm: formData.surface ? Number(formData.surface) : null,
        condition: formData.condition || null,
        amenities: formData.features || [],
        year_built: formData.yearBuilt || null,
        timing: formData.timing || null,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        additional_notes: formData.message || null,
      });
      setIsSubmitted(true);
      toast.success("Votre demande d'estimation a été envoyée");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.address.length > 0;
      case 2: return formData.propertyType.length > 0;
      case 3: return true; // Optional fields
      case 4: return formData.timing.length > 0;
      case 5: return formData.firstName && formData.lastName && formData.email;
      default: return true;
    }
  };

  // Success screen
  if (isSubmitted) {
    return (
      <main className="min-h-screen bg-byron-bg flex items-center justify-center" data-testid="valuation-success">
        <div className="max-w-xl mx-auto px-6 text-center py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 rounded-full bg-[#C6A87C]/20 flex items-center justify-center mx-auto mb-8">
              <Check size={32} className="text-[#C6A87C]" strokeWidth={1.5} />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-6">
              Merci pour votre demande
            </h1>
            <p className="text-[#666660] text-lg font-light leading-relaxed mb-8">
              Notre équipe analysera votre bien et vous contactera dans les plus brefs délais pour vous présenter une estimation personnalisée.
            </p>
            <button
              onClick={() => navigate("/")}
              className="text-sm uppercase tracking-[0.15em] text-[#1A1A1A] hover:text-[#C6A87C] transition-colors"
            >
              Retour à l'accueil
            </button>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-byron-bg" data-testid="valuation-page">
      <SEO 
        title="Estimation"
        description="Obtenez une estimation gratuite et confidentielle de votre bien immobilier à Genève. Nos experts évaluent votre propriété avec précision."
        canonical="/estimation"
        noindex={true}
      />
      {/* Header spacer */}
      <div className="header-spacer" />

      {/* Introduction - Centered text, no image, seamless light design */}
      <section className="py-10 md:py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-text mb-4">
              Estimation confidentielle
            </p>
            <h1 className="heading-primary mb-6">
              Estimez votre bien
            </h1>
            <p className="body-text max-w-lg mx-auto">
              Répondez à quelques questions pour recevoir une estimation personnalisée de votre propriété
            </p>
          </motion.div>
        </div>
      </section>

      {/* Progress Steps - Desktop */}
      <div className="bg-byron-bg border-b border-[#E8E6E1]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="hidden md:flex justify-center py-6">
            <div className="flex items-center gap-2">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => step.id <= currentStep && setCurrentStep(step.id)}
                    className={`flex items-center gap-2 px-3 py-2 transition-colors ${
                      step.id === currentStep
                        ? "text-[#1A1A1A]"
                        : step.id < currentStep
                        ? "text-[#C6A87C] cursor-pointer hover:text-[#1A1A1A]"
                        : "text-[#CCCCCC] cursor-default"
                    }`}
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                      step.id === currentStep
                        ? "bg-[#1A1A1A] text-white"
                        : step.id < currentStep
                        ? "bg-[#C6A87C] text-white"
                        : "bg-[#E8E6E1] text-[#999]"
                    }`}>
                      {step.id < currentStep ? <Check size={12} /> : step.id}
                    </span>
                    <span className="text-xs uppercase tracking-[0.1em]">{step.label}</span>
                  </button>
                  {index < steps.length - 1 && (
                    <div className={`w-8 h-px mx-2 ${
                      step.id < currentStep ? "bg-[#C6A87C]" : "bg-[#E8E6E1]"
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Progress */}
          <div className="md:hidden py-4">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs text-[#666660]">Étape {currentStep} / 5</span>
              <span className="text-xs font-medium text-[#1A1A1A]">{steps[currentStep - 1].label}</span>
            </div>
            <div className="h-1 bg-[#E8E6E1] rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#C6A87C] transition-all duration-300"
                style={{ width: `${(currentStep / 5) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="py-8 md:py-12">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-white border border-[#E8E6E1] p-8 md:p-12 min-h-[400px]">
            <AnimatePresence mode="wait">
              {/* Step 1: Location */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-8"
                >
                  <div className="text-center mb-8">
                    <h2 className="font-heading text-xl md:text-2xl font-light text-[#1A1A1A] mb-2">
                      Où se situe votre bien ?
                    </h2>
                    <p className="text-[#666660] font-light text-sm">
                      Indiquez l'adresse complète de votre propriété
                    </p>
                  </div>

                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(value) => updateFormData("address", value)}
                    placeholder="Adresse complète du bien"
                    variant="underline"
                    types={["address"]}
                    testId="valuation-address"
                  />

                  <p className="text-xs text-[#999] text-center">
                    Ces informations resteront strictement confidentielles
                  </p>
                </motion.div>
              )}

              {/* Step 2: Property Type */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-8"
                >
                  <div className="text-center mb-8">
                    <h2 className="font-heading text-xl md:text-2xl font-light text-[#1A1A1A] mb-2">
                      Quel type de bien possédez-vous ?
                    </h2>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {propertyTypes.map((type) => {
                      const IconComponent = type.icon;
                      return (
                        <button
                          key={type.id}
                          onClick={() => updateFormData("propertyType", type.id)}
                          className={`flex flex-col items-center gap-3 p-5 border transition-all ${
                            formData.propertyType === type.id
                              ? "border-[#C6A87C] bg-[#C6A87C]/5"
                              : "border-[#E8E6E1] hover:border-[#C6A87C]/50"
                          }`}
                          data-testid={`valuation-type-${type.id}`}
                        >
                          <IconComponent 
                            size={24} 
                            strokeWidth={1} 
                            className={formData.propertyType === type.id ? "text-[#C6A87C]" : "text-[#999]"}
                          />
                          <span className={`text-sm font-light ${
                            formData.propertyType === type.id ? "text-[#1A1A1A]" : "text-[#666660]"
                          }`}>
                            {type.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Step 3: Characteristics */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-8"
                >
                  <div className="text-center mb-8">
                    <h2 className="font-heading text-xl md:text-2xl font-light text-[#1A1A1A] mb-2">
                      Caractéristiques du bien
                    </h2>
                    <p className="text-[#666660] font-light text-sm">
                      Ces informations nous aident à affiner l'estimation
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Surface */}
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-[#666660] mb-2">
                        Surface habitable
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          value={formData.surface}
                          onChange={(e) => updateFormData("surface", e.target.value)}
                          placeholder="150"
                          className="w-full px-4 py-3 border-b border-[#E8E6E1] bg-transparent text-[#1A1A1A] placeholder:text-[#CCC] focus:outline-none focus:border-[#C6A87C] transition-colors font-light"
                          data-testid="valuation-surface"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999] text-sm">m²</span>
                      </div>
                    </div>

                    {/* Bedrooms & Rooms */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#666660] mb-2">
                          Chambres
                        </label>
                        <select
                          value={formData.bedrooms}
                          onChange={(e) => updateFormData("bedrooms", e.target.value)}
                          className="w-full px-4 py-3 border-b border-[#E8E6E1] bg-transparent text-[#1A1A1A] focus:outline-none focus:border-[#C6A87C] transition-colors font-light appearance-none cursor-pointer"
                          data-testid="valuation-bedrooms"
                        >
                          <option value="">Sélectionner</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#666660] mb-2">
                          Pièces
                        </label>
                        <select
                          value={formData.rooms}
                          onChange={(e) => updateFormData("rooms", e.target.value)}
                          className="w-full px-4 py-3 border-b border-[#E8E6E1] bg-transparent text-[#1A1A1A] focus:outline-none focus:border-[#C6A87C] transition-colors font-light appearance-none cursor-pointer"
                          data-testid="valuation-rooms"
                        >
                          <option value="">Sélectionner</option>
                          {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Condition */}
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-[#666660] mb-3">
                        État du bien
                      </label>
                      <div className="space-y-2">
                        {conditionOptions.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => updateFormData("condition", option.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 border transition-all text-left ${
                              formData.condition === option.id
                                ? "border-[#C6A87C] bg-[#C6A87C]/5"
                                : "border-[#E8E6E1] hover:border-[#C6A87C]/50"
                            }`}
                            data-testid={`valuation-condition-${option.id}`}
                          >
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                              formData.condition === option.id
                                ? "border-[#C6A87C] bg-[#C6A87C]"
                                : "border-[#CCCCCC]"
                            }`}>
                              {formData.condition === option.id && (
                                <Check size={10} className="text-white" strokeWidth={3} />
                              )}
                            </div>
                            <span className={`text-sm font-light ${
                              formData.condition === option.id ? "text-[#1A1A1A]" : "text-[#666660]"
                            }`}>
                              {option.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Timing */}
              {currentStep === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-8"
                >
                  <div className="text-center mb-8">
                    <h2 className="font-heading text-xl md:text-2xl font-light text-[#1A1A1A] mb-2">
                      Quand envisagez-vous de vendre ?
                    </h2>
                  </div>

                  <div className="space-y-3">
                    {timingOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => updateFormData("timing", option.id)}
                        className={`w-full flex items-center gap-4 px-6 py-4 border transition-all text-left ${
                          formData.timing === option.id
                            ? "border-[#C6A87C] bg-[#C6A87C]/5"
                            : "border-[#E8E6E1] hover:border-[#C6A87C]/50"
                        }`}
                        data-testid={`valuation-timing-${option.id}`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          formData.timing === option.id
                            ? "border-[#C6A87C] bg-[#C6A87C]"
                            : "border-[#CCCCCC]"
                        }`}>
                          {formData.timing === option.id && (
                            <Check size={12} className="text-white" strokeWidth={3} />
                          )}
                        </div>
                        <span className={`font-light ${
                          formData.timing === option.id ? "text-[#1A1A1A]" : "text-[#666660]"
                        }`}>
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 5: Contact */}
              {currentStep === 5 && (
                <motion.div
                  key="step5"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-8"
                >
                  <div className="text-center mb-8">
                    <h2 className="font-heading text-xl md:text-2xl font-light text-[#1A1A1A] mb-2">
                      Vos coordonnées
                    </h2>
                    <p className="text-[#666660] font-light text-sm">
                      Pour recevoir votre estimation personnalisée
                    </p>
                  </div>

                  <div className="space-y-5">
                    {/* Name Grid */}
                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#666660] mb-2">
                          Nom *
                        </label>
                        <input
                          type="text"
                          value={formData.lastName}
                          onChange={(e) => updateFormData("lastName", e.target.value)}
                          className="w-full px-4 py-3 border-b border-[#E8E6E1] bg-transparent text-[#1A1A1A] focus:outline-none focus:border-[#C6A87C] transition-colors font-light"
                          data-testid="valuation-lastname"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#666660] mb-2">
                          Prénom *
                        </label>
                        <input
                          type="text"
                          value={formData.firstName}
                          onChange={(e) => updateFormData("firstName", e.target.value)}
                          className="w-full px-4 py-3 border-b border-[#E8E6E1] bg-transparent text-[#1A1A1A] focus:outline-none focus:border-[#C6A87C] transition-colors font-light"
                          data-testid="valuation-firstname"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-[#666660] mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateFormData("email", e.target.value)}
                        className="w-full px-4 py-3 border-b border-[#E8E6E1] bg-transparent text-[#1A1A1A] focus:outline-none focus:border-[#C6A87C] transition-colors font-light"
                        data-testid="valuation-email"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-[#666660] mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => updateFormData("phone", e.target.value)}
                        className="w-full px-4 py-3 border-b border-[#E8E6E1] bg-transparent text-[#1A1A1A] focus:outline-none focus:border-[#C6A87C] transition-colors font-light"
                        data-testid="valuation-phone"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-[#666660] mb-2">
                        Message (optionnel)
                      </label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => updateFormData("message", e.target.value)}
                        rows={3}
                        placeholder="Informations complémentaires sur votre bien..."
                        className="w-full px-4 py-3 border-b border-[#E8E6E1] bg-transparent text-[#1A1A1A] placeholder:text-[#CCC] focus:outline-none focus:border-[#C6A87C] transition-colors font-light resize-none"
                        data-testid="valuation-message"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-8">
            <button
              onClick={prevStep}
              className={`flex items-center gap-2 text-sm uppercase tracking-[0.15em] transition-colors ${
                currentStep === 1
                  ? "text-[#CCCCCC] cursor-not-allowed"
                  : "text-[#666660] hover:text-[#1A1A1A]"
              }`}
              disabled={currentStep === 1}
              data-testid="valuation-back"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              Retour
            </button>

            {currentStep < 5 ? (
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className={`flex items-center gap-2 px-8 py-4 text-sm uppercase tracking-[0.15em] transition-colors ${
                  canProceed()
                    ? "bg-[#1A1A1A] text-white hover:bg-[#2A2A2A]"
                    : "bg-[#E8E6E1] text-[#999] cursor-not-allowed"
                }`}
                data-testid="valuation-next"
              >
                Étape suivante
                <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canProceed() || isSubmitting}
                className={`flex items-center gap-2 px-8 py-4 text-sm uppercase tracking-[0.15em] transition-colors ${
                  canProceed() && !isSubmitting
                    ? "bg-[#1A1A1A] text-white hover:bg-[#2A2A2A]"
                    : "bg-[#E8E6E1] text-[#999] cursor-not-allowed"
                }`}
                data-testid="valuation-submit"
              >
                {isSubmitting ? "Envoi en cours..." : "Envoyer la demande"}
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ValuationPage;
